/**
 * Moteur de scellement fiscal — orchestration hash + signature + chaînage
 * (CONFORMITE §2.1/§2.2, SPEC §14.1/§14.3).
 * Séparation stricte des responsabilités :
 *  - la lib SÉRIALISE (§2.3), HACHE (SHA-256) et CHAÎNE — déterministe, testable ;
 *  - la SIGNATURE est INJECTÉE (`FiscalSigner`) : en production, ECDSA P-256 (repli
 *    RSA-2048 PSS) dont la clé privée ne transite JAMAIS par le JS. Cette injection
 *    rend le moteur **100 % agnostique de la plateforme** (pur TS, zéro dépendance
 *    native) : le MÊME code fiscal tourne sur Android, iOS, un serveur Node/Web et
 *    Windows. Seul le `FiscalSigner` (porteur de clé) est spécifique à la plateforme :
 *    Android → Keystore (StrongBox/TEE) · iOS → Secure Enclave/Keychain · serveur Web →
 *    HSM/KMS (ou clé logicielle protégée) · Windows → CNG/TPM. Un fournisseur par cible,
 *    tous derrière cette même interface.
 *  - l'HEURE DE CONFIANCE et la PERSISTANCE (SQLite chiffré) restent côté produit :
 *    le produit persiste `getState()` dans la MÊME transaction que la pièce scellée.
 * Trois chaînes indépendantes par caisse : pièces, JET, Grands Totaux (§2.2).
 * ⚠️ Archive fiscale (§14.5.1bis) : la chaîne sérialisée `arg` est une donnée de
 * TRAVAIL (champs *-ARG) — seuls hash + signature entrent dans l'archive.
 */

import {
  GrandTotalData,
  GrandTotalType,
  JetData,
  PieceData,
  serializeGrandTotal,
  serializeJet,
  serializePiece,
  serializeReprint,
  type ReprintData,
} from './serialize';
import { sha256Hex } from './sha256';
import { jetEventByCode } from './jet';
import { GrandTotals, EMPTY_GRAND_TOTALS, PieceVatBlock, applySealedPiece, formatCumulParTaux, periodTotalsSince } from './grand-totals';

/**
 * Signataire injecté. `sign` reçoit le CONDENSAT hex (64 car.) et retourne la
 * signature encodée base64. `keyId` identifie la clé (rotation/audit R13).
 */
export interface FiscalSigner {
  readonly keyId: string;
  sign(hashHex: string): Promise<string>;
}

/** Résultat d'un scellement — à persister tel quel (hash+signature archivés, arg = travail) */
export interface SealedRecord {
  /** Chaîne sérialisée signée (§2.3) — donnée de travail *-ARG, EXCLUE de l'archive */
  arg: string;
  /** SHA-256(arg) hex */
  hash: string;
  /** Signature du hash (base64) — maillon de la chaîne suivante */
  signature: string;
  /** Clé utilisée */
  keyId: string;
  /** Signature du maillon précédent ('' pour le premier) */
  previousSignature: string;
}

/**
 * Maillon d'une chaîne de Grands Totaux d'UNE périodicité (R13 [6] : « la signature précédente est
 * celle du grand total précédent pour la même périodicité » — mars se chaîne à février, jamais à un
 * GT d'un autre type). La photographie des cumuls à la clôture précédente sert au calcul des
 * TOTAUX DE PÉRIODE (ENC-GTP-TTC/HT = delta entre deux clôtures de même périodicité).
 */
export interface GtChainRef {
  previousSignature: string;
  /** GTPCA photographié par la clôture précédente de cette périodicité (0 si première) */
  cumulTtcCents: number;
  /** Cumuls par taux photographiés par la clôture précédente ('' si première) */
  cumulParTaux: string;
}

/** État des chaînes + cumuls — LA vérité à persister après chaque scellement */
export interface FiscalChainState {
  piecePreviousSignature: string;
  jetPreviousSignature: string;
  /** 4ᵉ chaîne (v3) : DUPLICATA — R13 impose une chaîne de signatures dédiée (bloc `TAG-DUP-*`),
   *  distincte de celle des pièces : un duplicata ne s'intercale pas dans l'histoire des ventes. */
  reprintPreviousSignature: string;
  /** Chaînes de Grands Totaux PAR PÉRIODICITÉ (GTJ/GTM/GTA/GTS/GTT — R13 [6]) ; type absent = jamais clôturé */
  gtChains: Partial<Record<GrandTotalType, GtChainRef>>;
  /** Compteur de lignes JET (continu, démarre à 1) */
  jetLineCounter: number;
  totals: GrandTotals;
}

export const INITIAL_CHAIN_STATE: FiscalChainState = Object.freeze({
  piecePreviousSignature: '',
  jetPreviousSignature: '',
  reprintPreviousSignature: '',
  gtChains: {},
  jetLineCounter: 0,
  totals: EMPTY_GRAND_TOTALS,
});

export interface SealPieceInput extends PieceData {
  /** Blocs de ventilation (VatBreakdown.blocks de core-vat) — alimentent les cumuls par taux */
  vatBlocks: PieceVatBlock[];
  /** La pièce ANNULE une opération de trésorerie : hors grands totaux elle aussi (`countsInGrandTotals`). Hors charge signée. */
  cancelsTreasury?: boolean;
}

export class FiscalEngine {
  private state: FiscalChainState;

  constructor(private readonly signer: FiscalSigner, restoredState?: FiscalChainState) {
    const s = restoredState ?? INITIAL_CHAIN_STATE;
    this.state = {
      ...s,
      gtChains: Object.fromEntries(Object.entries(s.gtChains).map(([k, v]) => [k, { ...v }])),
      totals: { ...s.totals, perRate: s.totals.perRate.map(r => ({ ...r })) },
    };
  }

  /** État courant (copie) — à persister par le produit dans la même transaction que le scellement */
  getState(): FiscalChainState {
    const s = this.state;
    return {
      ...s,
      gtChains: Object.fromEntries(Object.entries(s.gtChains).map(([k, v]) => [k, { ...v }])),
      totals: { ...s.totals, perRate: s.totals.perRate.map(r => ({ ...r })) },
    };
  }

  /** Scelle une pièce commerciale : sérialise §2.3, hache, signe, chaîne, cumule (GTPCA/GTPVA/taux) */
  async sealPiece(input: SealPieceInput): Promise<SealedRecord> {
    const previousSignature = this.state.piecePreviousSignature;
    const arg = serializePiece(input, previousSignature);
    const hash = sha256Hex(arg);
    const signature = await this.signer.sign(hash);
    this.state = {
      ...this.state,
      piecePreviousSignature: signature,
      totals: applySealedPiece(this.state.totals, input),
    };
    return { arg, hash, signature, keyId: this.signer.keyId, previousSignature };
  }

  /** Scelle un événement JET (code contrôlé au catalogue R19, compteur de ligne auto) */
  async sealJetEvent(event: JetData): Promise<SealedRecord & { compteurLigne: number }> {
    jetEventByCode(event.codeEvenement);
    const compteurLigne = this.state.jetLineCounter + 1;
    const previousSignature = this.state.jetPreviousSignature;
    const arg = serializeJet(event, compteurLigne, previousSignature);
    const hash = sha256Hex(arg);
    const signature = await this.signer.sign(hash);
    this.state = { ...this.state, jetPreviousSignature: signature, jetLineCounter: compteurLigne };
    return { arg, hash, signature, keyId: this.signer.keyId, previousSignature, compteurLigne };
  }

  /**
   * Scelle un DUPLICATA (4ᵉ chaîne). Le §6.11.3.6 range le duplicata parmi les objets soumis à
   * signature au même rang que les ventes, le JET et les Grands Totaux : le COMPTER (`fiscal_reprint`)
   * et le JOURNALISER (JET 155) sont deux obligations distinctes, aucune ne remplace le scellement.
   * Chaîne DÉDIÉE : un duplicata ne s'intercale pas dans l'histoire des ventes.
   */
  async sealReprint(input: ReprintData): Promise<SealedRecord> {
    const previousSignature = this.state.reprintPreviousSignature;
    const arg = serializeReprint(input, previousSignature);
    const hash = sha256Hex(arg);
    const signature = await this.signer.sign(hash);
    this.state = { ...this.state, reprintPreviousSignature: signature };
    return { arg, hash, signature, keyId: this.signer.keyId, previousSignature };
  }

  /**
   * Scelle un Grand Total : PHOTOGRAPHIE des cumuls perpétuels courants — ne remet RIEN à zéro
   * (§14.3) — PLUS les totaux de la PÉRIODE close (v2, R13 ENC-GTP-TTC/HT : delta vs la clôture
   * précédente de MÊME périodicité). Niveaux R13 : `GTT` (à chaque pièce, `periodeId` = n° de la
   * pièce), périodiques `GTJ`/`GTM`/`GTA` (periodeId daté), `GTS` (session « Z », periodeId = n° du Z).
   * ⚠️ CHAÎNAGE PAR PÉRIODICITÉ (R13 [6]) : la signature précédente est celle du dernier GT du MÊME
   * type — le GTJ de mardi se chaîne au GTJ de lundi, même si des GTS/GTM se sont intercalés.
   */
  async sealGrandTotal(typeGT: GrandTotalType, periodeId: string, registerId: string, dateHeure: string): Promise<SealedRecord & { data: GrandTotalData }> {
    const chain = this.state.gtChains[typeGT] ?? { previousSignature: '', cumulTtcCents: 0, cumulParTaux: '' };
    const period = periodTotalsSince(this.state.totals, chain.cumulTtcCents, chain.cumulParTaux);
    const data: GrandTotalData = {
      typeGT,
      periodeId,
      registerId,
      dateHeure,
      cumulTtcCents: this.state.totals.gtpcaCents,
      cumulTtcAbsCents: this.state.totals.gtpvaCents,
      cumulParTaux: formatCumulParTaux(this.state.totals),
      compteurPieces: this.state.totals.pieceCount,
      periodeTtcCents: period.ttcCents,
      periodeHtCents: period.htCents,
      periodeParTaux: period.parTaux,
    };
    const previousSignature = chain.previousSignature;
    const arg = serializeGrandTotal(data, previousSignature);
    const hash = sha256Hex(arg);
    const signature = await this.signer.sign(hash);
    this.state = {
      ...this.state,
      gtChains: {
        ...this.state.gtChains,
        [typeGT]: { previousSignature: signature, cumulTtcCents: data.cumulTtcCents, cumulParTaux: data.cumulParTaux },
      },
    };
    return { arg, hash, signature, keyId: this.signer.keyId, previousSignature, data };
  }
}

// ─── Vérification de chaîne (Inspecteur NF525 §12, JET 90) ───────────────────

/**
 * LE MARQUEUR DE TÊTE DE CHAQUE CHAÎNE — ce que la PREMIÈRE ligne porte comme report.
 *
 * ⚠️ 🔴 LES TROIS CHAÎNES N'ONT PAS LA MÊME CONVENTION, ET C'EST MESURÉ EN PRODUCTION : sur une base
 * magasin réelle, la première PIÈCE porte `GENESIS`, tandis que le premier GRAND TOTAL et la première
 * ligne de JET portent une chaîne VIDE. Ce n'est pas un choix qu'on rattrape : ces valeurs sont
 * entrées dans des charges SIGNÉES et append-only — les uniformiser demanderait de re-signer, ce que
 * personne ne peut faire.
 *
 * ⚠️ 🔴 CETTE TABLE DÉCRIT L'EXISTANT, ELLE NE LE CORRIGE PAS. Sa raison d'être est qu'un vérificateur
 * écrit pour une chaîne ne soit pas employé tel quel sur une autre : `verifyChain` prend `''` par
 * défaut, ce qui convient à DEUX chaînes sur trois — donc un vérificateur de pièces écrit sans y
 * penser marche, et un vérificateur de grands totaux écrit AVEC le marqueur des pièces déclarerait
 * rompue la première ligne de chaque caisse.
 *
 * ⚠️ LE DÉFAUT SILENCIEUX QU'ELLE FERME : le mauvais marqueur ne produit pas d'erreur, il produit UNE
 * rupture — à l'index 0, sur une chaîne parfaitement saine. Sur un tableau de bord d'intégrité, c'est
 * un rouge permanent qu'on finit par ignorer.
 */
export const CHAIN_HEAD_MARKERS = {
  /** Les pièces : la caisse écrit `GENESIS` faute de prédécesseur. Vérifié sur base de production. */
  fiscal_piece: 'GENESIS',
  /**
   * Les duplicata — la 4ᵉ chaîne signée (§6.11.3.6 la range avec les ventes, le JET et les grands
   * totaux). Même convention que les pièces, LUE DANS LE CODE du scellement : aucune base de la bêta
   * n'en porte encore, donc c'est l'écrivain qui fait foi ici, pas une mesure.
   * ⚠️ Elle manquait à la première version de cette table, et c'est ce qui prouve son utilité : quatre
   * chaînes se comptent mal de tête.
   */
  fiscal_reprint: 'GENESIS',
  /** Les grands totaux : report VIDE sur la première clôture. Vérifié sur base de production. */
  fiscal_grand_total: '',
  /** Le journal des événements : report VIDE sur la première ligne. Vérifié sur base de production. */
  fiscal_jet: '',
} as const;

/** Les chaînes scellées dont on connaît le marqueur de tête. */
export type SealedChain = keyof typeof CHAIN_HEAD_MARKERS;

/**
 * Le report attendu sur la PREMIÈRE ligne d'une chaîne.
 * ⚠️ À passer à `verifyChain` en second argument : s'en remettre à son défaut revient à supposer la
 * convention des grands totaux, et donc à se tromper sur les pièces.
 */
export const chainHeadMarker = (chain: SealedChain): string => CHAIN_HEAD_MARKERS[chain];

/**
 * 🔴 LE REPORT QUI EST ENTRÉ DANS LA SIGNATURE, déduit de celui qui est STOCKÉ.
 *
 * ⚠️ 🔴 LA TABLE CI-DESSUS DÉCRIT CE QUI EST **STOCKÉ**, PAS CE QUI EST **SIGNÉ**, et les deux diffèrent
 * sur la première ligne. À la genèse l'état de chaîne porte une chaîne VIDE
 * (`INITIAL_CHAIN_STATE.piecePreviousSignature = ''`), donc la charge signée de la toute première pièce
 * se termine par `|` — puis le DAO la NORMALISE en `GENESIS` pour la colonne. Les deux conventions
 * cohabitent, et rien ne le disait.
 *
 * ⚠️ 🔴 CE QUE CETTE CONFUSION A COÛTÉ, MESURÉ SUR LA PRODUCTION : le constat de chaîne du siège
 * comparait la fin de la charge signée à `'|' || prev_signature` **en lisant la colonne** — donc il
 * déclarait « charge signée discordante » sur la PREMIÈRE pièce de CHAQUE caisse, c'est-à-dire un
 * verdict FAUTIF permanent sur une chaîne parfaitement saine. ⚠️ Et le banc ne pouvait pas le voir : il
 * fabriquait ses charges avec la même croyance que le code (`…|GENESIS`). *Une doublure n'est pas une
 * preuve — seule la donnée réelle a tranché (25 charges sur 26 reproduites au caractère près, la 26ᵉ
 * étant la première de la chaîne).*
 *
 * ⚠️ Pour les grands totaux et le JET, stocké et signé valent tous deux la chaîne vide : la fonction est
 * alors l'identité, et c'est bien ce qu'on veut — un seul geste pour les quatre chaînes.
 */
export const signedPrevious = (chain: SealedChain, stored: string): string =>
  (stored === CHAIN_HEAD_MARKERS[chain] ? '' : stored);

export interface ChainLink {
  arg: string;
  hash: string;
  previousSignature: string;
  signature: string;
}

export interface ChainVerdict {
  ok: boolean;
  checked: number;
  /** Index (0-based) et nature des ruptures détectées */
  breaks: { index: number; reason: 'hash_mismatch' | 'link_mismatch' | 'arg_prev_mismatch' }[];
}

/**
 * Vérifie l'intégrité d'une chaîne scellée (pièces, JET ou GT — même mécanique) :
 *  1. hash stocké = SHA-256(arg) recalculé (altération du contenu) ;
 *  2. previousSignature du maillon n = signature du maillon n−1 (rupture de chaîne) ;
 *  3. arg se termine bien par `|previousSignature` (cohérence donnée signée / lien).
 * La vérification CRYPTOGRAPHIQUE des signatures (clé publique) est faite côté
 * produit/auditeur — hors périmètre de la lib (la clé vit dans le Keystore).
 */
export function verifyChain(links: ChainLink[], firstPreviousSignature = ''): ChainVerdict {
  const breaks: ChainVerdict['breaks'] = [];
  let expectedPrev = firstPreviousSignature;
  links.forEach((link, index) => {
    if (sha256Hex(link.arg) !== link.hash) breaks.push({ index, reason: 'hash_mismatch' });
    if (link.previousSignature !== expectedPrev) breaks.push({ index, reason: 'link_mismatch' });
    if (!link.arg.endsWith(`|${link.previousSignature}`)) breaks.push({ index, reason: 'arg_prev_mismatch' });
    expectedPrev = link.signature;
  });
  return { ok: breaks.length === 0, checked: links.length, breaks };
}
